import { formatShortDate, formatTime } from "@clipboard-health/date-time";
import { isDefined, isNil } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { Divider, Text } from "@src/appV2/redesign/system/ui-components";
import { formatToHoursAndMinutes } from "@src/appV2/redesign/utils/formatToHoursAndMinutes";
import { roundUpHoursToTwoDecimalsUsingCbhRounding } from "@src/appV2/Shifts/utils/timeCalculation";
import { differenceInSeconds, hoursToMinutes, hoursToSeconds, isSameDay } from "date-fns";

import { type PaymentsListItemDetailed } from "../types/PaymentsListItemDetailed";
import { PaymentDetailsAdjustmentLineItem } from "./PaymentDetailsAdjustmentLineItem";
import { PaymentDetailsLineItem } from "./PaymentDetailsLineItem";

export interface PaymentDetailsPaymentBreakdownSectionProps {
  item: PaymentsListItemDetailed;
  payoutFeeInCents?: number;
  ten99PolicyFeeInCents?: number;
}

export function PaymentDetailsPaymentBreakdownSection(
  props: PaymentDetailsPaymentBreakdownSectionProps
) {
  const { item, payoutFeeInCents = 0, ten99PolicyFeeInCents = 0 } = props;
  const netAmountInCents = item.attributes.amountInCents - payoutFeeInCents;
  const hasAnyFee = payoutFeeInCents > 0 || ten99PolicyFeeInCents > 0;

  const categoryLineItems = getCategorySpecificLineItems(item);
  if (categoryLineItems.length === 0 && !hasAnyFee) {
    return null;
  }

  return (
    <Stack spacing={8}>
      <Divider />
      <Text semibold variant="h6">
        {item.attributes.amountInCents > 0 ? "Payment" : "Reversal"} breakdown
      </Text>
      <Stack spacing={6}>
        {categoryLineItems}
        {hasAnyFee && (
          <PaymentDetailsLineItem
            iconType="coin-filled"
            label="Gross amount"
            value={formatDollarsAsUsd(
              (item.attributes.amountInCents + ten99PolicyFeeInCents) / 100
            )}
          />
        )}
        {ten99PolicyFeeInCents > 0 && (
          <PaymentDetailsLineItem
            iconType="briefcase-filled"
            label="Workers' compensation coverage fee"
            value={formatDollarsAsUsd(-ten99PolicyFeeInCents / 100)}
          />
        )}
        {payoutFeeInCents > 0 && (
          <PaymentDetailsLineItem
            iconType="bolt-filled"
            label="InstantPay fee"
            value={formatDollarsAsUsd(-payoutFeeInCents / 100)}
          />
        )}
      </Stack>
      <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
        <Text semibold variant="h6" sx={(theme) => ({ color: theme.palette.text.secondary })}>
          Net amount
        </Text>
        <Text semibold variant="h5">
          {formatDollarsAsUsd(netAmountInCents / 100)}
        </Text>
      </Stack>
    </Stack>
  );
}

function getCategorySpecificLineItems(item: PaymentsListItemDetailed): JSX.Element[] {
  switch (item.attributes.paymentCategory) {
    case "shift-verification": {
      if (isNil(item.shift)) {
        return [];
      }

      return [
        <PaymentDetailsLineItem
          key="hours-worked"
          iconType="clock-filled"
          label="Hours worked"
          value={formatToHoursAndMinutes(hoursToSeconds(item.shift.attributes.time))}
        />,
        <PaymentDetailsLineItem
          key="pay-rate"
          iconType="coin-filled"
          label="Pay rate"
          value={`${formatDollarsAsUsd(item.shift.attributes.pay)}/hr`}
        />,
      ];
    }

    case "shift-cancellation":
    case "shift-sent-home": {
      if (isNil(item.shift)) {
        return [];
      }

      return [
        <PaymentDetailsLineItem
          key="paid-hours"
          iconType="clock-filled"
          label="Paid hours"
          labelSecondLine={
            item.attributes.paymentCategory === "shift-cancellation"
              ? "Cancelled shift compensation"
              : "Sent home compensation"
          }
          value={formatToHoursAndMinutes(hoursToSeconds(item.shift.attributes.time))}
        />,
        <PaymentDetailsLineItem
          key="pay-rate"
          iconType="coin-filled"
          label="Pay rate"
          value={`${formatDollarsAsUsd(item.shift.attributes.pay)}/hr`}
        />,
      ];
    }

    case "shift-adjustment": {
      if (isNil(item.shiftAdjustment)) {
        return [];
      }

      const adjustedMinutesDifference = isDefined(item.shiftAdjustment.attributes.time.new)
        ? hoursToMinutes(item.shiftAdjustment.attributes.time.new) -
          hoursToMinutes(item.shiftAdjustment.attributes.time.old)
        : 0;

      const adjustedPayDifference = isDefined(item.shiftAdjustment.attributes.pay.new)
        ? item.shiftAdjustment.attributes.pay.new - item.shiftAdjustment.attributes.pay.old
        : 0;

      return [
        isDefined(item.shiftAdjustment.attributes.time.new) ? (
          <PaymentDetailsAdjustmentLineItem
            key="adjusted-hours"
            iconType="clock-filled"
            label="Adjusted hours"
            newValue={formatToHoursAndMinutes(
              hoursToMinutes(item.shiftAdjustment.attributes.time.new) * 60
            )}
            oldValue={formatToHoursAndMinutes(
              hoursToMinutes(item.shiftAdjustment.attributes.time.old) * 60
            )}
            difference={`${adjustedMinutesDifference >= 0 ? "+" : "-"}${formatToHoursAndMinutes(
              Math.abs(adjustedMinutesDifference) * 60
            )}`}
            differenceType={adjustedMinutesDifference >= 0 ? "positive" : "negative"}
          />
        ) : (
          <PaymentDetailsLineItem
            key="hours-worked"
            iconType="clock-filled"
            label="Hours worked"
            value={formatToHoursAndMinutes(
              hoursToSeconds(item.shiftAdjustment.attributes.time.old)
            )}
          />
        ),
        isDefined(item.shiftAdjustment.attributes.pay.new) ? (
          <PaymentDetailsAdjustmentLineItem
            key="adjusted-rate"
            iconType="coin-filled"
            label="Adjusted pay rate"
            newValue={formatDollarsAsUsd(item.shiftAdjustment.attributes.pay.new)}
            oldValue={formatDollarsAsUsd(item.shiftAdjustment.attributes.pay.old)}
            difference={`${formatDollarsAsUsd(adjustedPayDifference, { signDisplay: "always" })}`}
            differenceType={adjustedPayDifference >= 0 ? "positive" : "negative"}
          />
        ) : (
          <PaymentDetailsLineItem
            key="pay-rate"
            iconType="coin-filled"
            label="Pay rate"
            value={`${formatDollarsAsUsd(item.shiftAdjustment.attributes.pay.old)}/hr`}
          />
        ),
      ];
    }

    case "worked-break": {
      if (isNil(item.workedBreak) || isNil(item.shift)) {
        return [];
      }

      const workedBreakStart = new Date(item.workedBreak.attributes.start);
      const workedBreakEnd = new Date(item.workedBreak.attributes.end);

      const workedBreakDurationInSeconds = differenceInSeconds(workedBreakEnd, workedBreakStart);

      const adjustedHours = roundUpHoursToTwoDecimalsUsingCbhRounding(
        item.shift.attributes.time + workedBreakDurationInSeconds / 3600
      );

      return [
        <PaymentDetailsLineItem
          key="worked-break"
          iconType="clock-filled"
          label="Worked break"
          value={`${formatTime(workedBreakStart)} - ${formatTime(workedBreakEnd)}`}
          valueSecondLine={formatToHoursAndMinutes(workedBreakDurationInSeconds)}
        />,
        <PaymentDetailsAdjustmentLineItem
          key="adjusted-hours"
          iconType="clock-filled"
          label="Adjusted hours"
          newValue={formatToHoursAndMinutes(hoursToSeconds(adjustedHours))}
          oldValue={formatToHoursAndMinutes(hoursToSeconds(item.shift.attributes.time))}
          difference={`+${formatToHoursAndMinutes(workedBreakDurationInSeconds)}`}
          differenceType="positive"
        />,
        <PaymentDetailsLineItem
          key="pay-rate"
          iconType="coin-filled"
          label="Pay rate"
          value={`${formatDollarsAsUsd(item.shift.attributes.pay)}/hr`}
        />,
      ];
    }

    case "etp-bonus": {
      if (isNil(item.extraTimePay)) {
        return [];
      }

      const etpPeriodStart = new Date(item.extraTimePay.attributes.periodStart);
      const etpPeriodEnd = new Date(item.extraTimePay.attributes.periodEnd);

      return [
        <PaymentDetailsLineItem
          key="total-worked-hours"
          iconType="clock-filled"
          label="Total worked hours"
          labelSecondLine={
            isSameDay(etpPeriodStart, etpPeriodEnd)
              ? `On ${formatShortDate(etpPeriodStart)}`
              : `From ${formatShortDate(etpPeriodStart)} to ${formatShortDate(etpPeriodEnd)}`
          }
          value={formatToHoursAndMinutes(
            hoursToSeconds(
              item.extraTimePay.attributes.threshold +
                item.extraTimePay.attributes.hoursAboveThreshold
            )
          )}
        />,
        <PaymentDetailsLineItem
          key="etp-eligible-hours"
          iconType="clock-filled"
          label="ETP eligible hours"
          value={formatToHoursAndMinutes(
            hoursToSeconds(item.extraTimePay.attributes.hoursAboveThreshold)
          )}
        />,
      ];
    }

    case "manual-bonus":
    case "cancellation-protection-bonus":
    case "other":
      return [];

    default: {
      return item.attributes.paymentCategory satisfies never;
    }
  }
}
